import {
	Icon,
	ListItem,
	ListItemIcon,
	Stack,
	Tooltip,
	Typography,
	Alert,
} from '@mui/material';

import { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { ReactComponent as ErrorIcon } from 'common/assets/images/alert-error.svg';
import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import { ReactComponent as EditIcon } from 'common/assets/images/edit-Icon.svg';
import SideDrawer from 'common/components/SideDrawer/SideDrawer';
import { Column } from 'common/components/Table/CommonTableStyles';
import TableComponent from 'common/components/Table/Table';
import AlertComponent from 'common/components/ToastMessage/ToastMessage';
import { generateEllipsisTooltip } from 'common/components/Tooltip/EllipsisTooltip';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	APP_GENERAL_ERROR_MSG,
	GENERIC_ERROR_MSG,
} from 'common/constants/TextContent';
import { WORKFLOW_TEXT_CONSTANTS } from 'common/constants/WorkflowConstants';
import { RootState } from 'common/store/AppStore';
import { setToastMessage } from 'common/store/reducer/commonReducer';
import {
	setCreateQueueFlag,
	setQueueDrawer,
	setQueueDetails,
	setEditQueueFlag,
} from 'common/store/reducer/workflowReducer';
import {
	dateInCorrectFormatWithFullYear,
	toSentenceCase,
} from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import ActionsPrompt from './ActionsPrompt';
import { deleteQueue, getAllQueueList, getQueue } from './api';
import { IQueueTableRow, IWorkflowStepInfo } from './api/type-def';
import CreateQueue from './Queue/CreateQueue';

import styles from './QueueList.module.scss';

const {
	QUEUE_DELETED,
	DELETE_CONFIRMATION,
	DELETE_TITLE,
	DELETE_SUB_TITLE,
	DELETE_BUTTON_CANCEL_LABEL,
	DELETE_BUTTON_LABEL,
} = WORKFLOW_TEXT_CONSTANTS;

const { OK, NOT_FOUND } = HTTP_STATUS_CODES;

const QueueList = () => {
	const dispatch = useDispatch();
	const [isLoading, setLoading] = useState<boolean>(false);
	const [queueList, setQueueList] = useState<IQueueTableRow[]>([]);
	const [hoverIcon, setHoverIcon] = useState<boolean>(false);
	const [selectedQueueIndex, setSelectedQueueIndex] = useState<string>('');
	const [totalPagesCount, setTotalPagesCount] = useState<number>(0);
	const [totalRecordsCount, setTotalRecordsCount] = useState<number>(0);
	const [selectedQueue, setSelectedQueue] = useState<IQueueTableRow | null>(
		null,
	);
	const [openActionsModal, setOpenActionsModal] = useState<boolean>(false);

	const { tableQueryObj, updateTableQuery } = useTableQuery({
		id: 'queueListTable',
	});
	const { severity, isToastOpen, message } = useSelector(
		(state: RootState) => state.common.toastMessage,
	);
	const paramsData = useSelector((state: RootState) => state.common.pagination);

	const createQueueFlag = useSelector(
		(state: RootState) => state.workflowmodule.createQueueFlag,
	);

	const editQueueFlag = useSelector(
		(state: RootState) => state.workflowmodule.editQueueFlag,
	);

	const openQueueDrawer = useSelector(
		(state: RootState) => state.workflowmodule.queueDrawer,
	);

	const { statusWarningColor, textColor, iconFillColor } = styles;

	const getQueueList = async (paramsData: any) => {
		try {
			setLoading(true);
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			});
			const resp: any = await getAllQueueList(paramsData);
			if (resp?.statusCode === OK) {
				let result = resp?.responseBody.content;
				result = result.map((queue: IQueueTableRow) => ({
					...queue,
					id: queue.queueId,
				}));
				setTotalPagesCount(resp?.responseBody?.totalPages);
				setTotalRecordsCount(resp?.responseBody?.totalCount);
				setQueueList(result);
				setLoading(false);
			} else if (resp?.statusCode === NOT_FOUND) {
				setLoading(false);
			} else {
				setLoading(false);
				dispatch(
					setToastMessage({
						message: APP_GENERAL_ERROR_MSG,
						severity: 'error',
						isToastOpen: true,
					}),
				);
			}
		} catch (e) {
			setLoading(false);
			dispatch(
				setToastMessage({
					message: GENERIC_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const handlePageChange = (data: any) => {
		updateTableQuery(data);
		getQueueList(data);
	};

	const handleSortChange = (data: any) => {
		getQueueList(data);
	};

	const handleClose = () => {
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
		dispatch(
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			}),
		);
	};

	const setListHover = (index: string) => {
		setHoverIcon(true);
		setSelectedQueueIndex(index);
	};

	const onHideListIcons = () => {
		setHoverIcon(false);
	};

	const onDeleteQueue = (data: IQueueTableRow) => {
		setSelectedQueue(data);
		setOpenActionsModal(true);
	};

	const onEditQueue = async (data: IQueueTableRow) => {
		try {
			const queueResponse = await getQueue({ queueId: data.queueId });
			if (queueResponse?.statusCode === OK) {
				dispatch(
					setQueueDetails({
						type: 'edit',
						data: queueResponse?.responseBody,
					}),
				);

				dispatch(setQueueDrawer(true));
			}
		} catch (e) {
			dispatch(
				setToastMessage({
					message: GENERIC_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};

	const getAlertText = (data: string) =>
		`${DELETE_CONFIRMATION} selected ${data}?`;

	const cancelHandler = () => {
		setOpenActionsModal(false);
		setSelectedQueue(null);
	};

	const resetQueueList = async (selectedQueue: IQueueTableRow) => {
		dispatch(
			setToastMessage({
				message: QUEUE_DELETED,
				severity: 'success',
				isToastOpen: true,
			}),
		);
		setQueueList((prevState: IQueueTableRow[]) => {
			const prevData = [...prevState];
			const updatedData = prevData.filter(
				(queue) => queue.queueId !== selectedQueue.queueId,
			);
			return updatedData;
		});
	};

	const actionHandler = async (data: IQueueTableRow) => {
		const resp: any = await deleteQueue(data);
		if (resp?.statusCode === OK) {
			setOpenActionsModal(false);
			setLoading(false);
			resetQueueList(data);
		} else {
			setOpenActionsModal(false);
			setSelectedQueue(null);
			setLoading(false);
			dispatch(
				setToastMessage({
					message: APP_GENERAL_ERROR_MSG,
					severity: 'error',
					isToastOpen: true,
				}),
			);
		}
	};
	useEffect(
		() => () => {
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			});
			setToastMessage({
				message: '',
				severity: '',
				isToastOpen: false,
			});
		},
		[],
	);
	useEffect(() => {
		if (createQueueFlag) {
			const data = {
				sortKey: 'createdDate',
				pageSize: paramsData.pageSize,
				pageNumber: 0,
				sortDirection: 'desc',
			};
			getQueueList(data);
			dispatch(setCreateQueueFlag(false));
		}
	}, [createQueueFlag]);
	useEffect(() => {
		if (editQueueFlag) {
			const updatedQuery = {
				pageSize: paramsData.pageSize,
				pageNumber: 0,
				sortKey: 'modifiedDate',
				sortDirection: 'desc',
			};
			getQueueList(updatedQuery);
			dispatch(setEditQueueFlag(false));
		}
	}, [editQueueFlag]);
	const queueColumns: readonly Column[] = [
		{
			id: 'queueName',
			label: 'Queue',
			disablePadding: false,
			isSortable: true,
			render: (params: any) => (
				<Typography
					className={styles['queue-list-name']}
					data-testid="queue-listname"
				>
					{params?.queueName}
				</Typography>
			),
		},
		{
			id: 'workflowSteps',
			label: toSentenceCase('Workflow Steps'),
			disablePadding: false,
			isSortable: false,
			render: ({ workflowSteps }: any) =>
				generateEllipsisTooltip({
					items: workflowSteps.map((workflowStep: IWorkflowStepInfo) => ({
						name: workflowStep.workflowStepName,
					})),
					maxVisibleItems: 2,
					dataTestId: 'workflow-steps-tooltip',
				}),
		},
		{
			id: 'userCount',
			label: 'Users',
			disablePadding: false,
			isSortable: true,
			render: ({ userCount }: any) => <Typography>{userCount}</Typography>,
		},
		{
			id: 'createdDate',
			label: toSentenceCase('Created Date'),
			disablePadding: false,
			isSortable: true,
			render: (row: any) => (
				<Typography>
					{row?.createdDate &&
						dateInCorrectFormatWithFullYear(row?.createdDate)}
				</Typography>
			),
		},
		{
			id: 'modifiedDate',
			label: toSentenceCase('Last Updated Date'),
			disablePadding: false,
			isSortable: true,
			render: (row: any) => (
				<Typography>
					{row?.modifiedDate &&
						dateInCorrectFormatWithFullYear(row?.modifiedDate)}
				</Typography>
			),
		},
		{
			id: 'id',
			label: 'Action',
			disablePadding: false,
			isSortable: false,
			render: (row: any) => (
				<ListItem
					className={styles['queue-icon-actions']}
					data-testid="list-actions"
					onMouseOver={() => {
						setListHover(row.queueId);
					}}
					onFocus={() => {
						setListHover(row.queueId);
					}}
					onMouseOut={() => {
						onHideListIcons();
					}}
					onBlur={() => {
						onHideListIcons();
					}}
				>
					{hoverIcon && selectedQueueIndex === row.queueId ? (
						<ListItemIcon
							sx={{
								'& :hover': {
									cursor: 'not-allowed',
								},
								paddingLeft: '0px',
							}}
						>
							<Stack direction="row" className={styles['queue-list-item']}>
								<Tooltip
									title="Edit"
									placement="top"
									data-testid="edit-tooltip"
									className={styles['info-icon']}
								>
									<Icon
										data-testid="edit-icon"
										onClick={() => onEditQueue(row)}
									>
										<EditIcon fill={iconFillColor} />
									</Icon>
								</Tooltip>
								<Tooltip
									title="Delete"
									placement="top"
									data-testid="delete-tooltip"
									className={styles['info-icon']}
								>
									<Icon
										className={styles['queue-list-item-delete']}
										data-testid="delete-icon"
										onClick={() => onDeleteQueue(row)}
									>
										<DeleteIcon fill={iconFillColor} />
									</Icon>
								</Tooltip>
							</Stack>
						</ListItemIcon>
					) : null}
				</ListItem>
			),
		},
	];

	return (
		<div className={styles['queue-list-container']}>
			<TableComponent
				defaultQuery={tableQueryObj}
				rows={queueList}
				columns={queueColumns}
				totalItems={totalRecordsCount}
				totalPagesCount={totalPagesCount}
				defaultSortKey=""
				onPageHandler={handlePageChange}
				onSortHandler={handleSortChange}
				isLoading={isLoading}
				tableStyles={{ minWidth: '46.875em', maxHeight: '27.5em' }}
				enableHeaderColor
				enableInnerTableRow={false}
				colSpan={3}
				isRenderPagination
			/>
			<AlertComponent
				open={isToastOpen ?? false}
				toastMessage={message ?? ''}
				severity={severity ?? ''}
				handleCloseMessage={handleClose}
				vertical="top"
				horizontal="right"
			/>
			{openActionsModal && (
				<ActionsPrompt
					mainTitle={DELETE_TITLE}
					mainTitleCss={styles['queue-listactions-prompt-main-title']}
					openModal={openActionsModal}
					showCloseIcon
					onCloseModal={cancelHandler}
					data={selectedQueue}
					subTitle={getAlertText(selectedQueue?.queueName ?? '')}
					alertTemplate={
						<Alert
							icon={
								<ErrorIcon
									fill={statusWarningColor}
									className={styles['actions-prompt-alert-icon']}
								/>
							}
							sx={{
								color: textColor,
								'&.MuiAlert-root': {
									background: statusWarningColor,
								},
								'&.MuiAlert-icon': {
									color: `${statusWarningColor} !important`,
								},
							}}
						>
							{DELETE_SUB_TITLE}
						</Alert>
					}
					actionHandler={actionHandler}
					buttonsLabel={[DELETE_BUTTON_CANCEL_LABEL, DELETE_BUTTON_LABEL]}
				/>
			)}
			{openQueueDrawer && (
				<SideDrawer
					className={styles['edit-queue-drawer']}
					drawerState={openQueueDrawer}
					data-testid="edit-queue-drawer"
				>
					<CreateQueue />
				</SideDrawer>
			)}
		</div>
	);
};

export default QueueList;
